/**
 * Tri-state permission model + per-slice paywall policy.
 *
 * Dependency-free leaf module: the provider flags are undefined until the
 * public-data endpoint returns, and "unknown" must NOT be conflated with
 * "denied". Living here (not in the appState slice) lets both the slice and
 * configUtils share the constants without a circular import.
 */
export const PERMISSION = {
  UNKNOWN: 'unknown',
  ALLOWED: 'allowed',
  DENIED: 'denied',
};

/** Maps a raw provider flag (boolean | undefined) to a tri-state value. */
export const toPermissionState = (value) => {
  if (value === undefined || value === null) return PERMISSION.UNKNOWN;
  return value ? PERMISSION.ALLOWED : PERMISSION.DENIED;
};

/**
 * Per-slice paywall policy, gated by the reversibility of applying the slice:
 *   - OPTIMISTIC (permitted unless explicitly DENIED) for reversible presentation
 *     (theming, fields) — reverting is a clean re-dispatch, so keep during 'unknown'.
 *   - PESSIMISTIC (permitted only when explicitly ALLOWED) for localization, whose
 *     i18n bundle application is an irreversible additive side effect — applying it
 *     during 'unknown' and then discovering 'denied' would be a paywall bypass.
 *
 * This is the single source of truth for the decision; the two call sites
 * (ConfigHandler.mergeConfigs, configUtils.processProductOverride) keep their own
 * distinct *apply* mechanics.
 */
const OPTIMISTIC_SLICES = { theming: true, fields: true, localisation: false };

export const isSlicePermitted = (slice, state) =>
  OPTIMISTIC_SLICES[slice]
    ? state !== PERMISSION.DENIED
    : state === PERMISSION.ALLOWED;
